import { Button, TextField } from 'components';
import { useAnalytics } from 'contexts/Analytics/Analytics';
import { useApi } from 'contexts/Api/Api';
import { useCalculator } from 'contexts/Calculator/Calculator';
import { savePdf, saveText } from 'helpers';
import { useState } from 'react';
import { DISTRIBUTION_FUNCTIONS, GOALS } from '../../constants';

export default function Export() {
    const [copied, setCopied] = useState(false);
    const analytics = useAnalytics();
    const api = useApi();
    const calculator = useCalculator();

    async function handleCopyUrl() {
        void analytics.trackActionEvent('export_share_clicked', {
            export_action: 'copy_url',
            resource_id: 'finish_export',
            resource_type: 'tool',
            source: 'finish_export',
        });
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
        } catch {
            // Clipboard access denied (e.g. insecure context, no user gesture, iframe policy)
        }
    }

    function handleDownloadPdf() {
        void analytics.trackActionEvent('export_share_clicked', {
            export_action: 'download_pdf',
            resource_id: 'finish_export',
            resource_type: 'tool',
            source: 'finish_export',
        });

        // Check authentication
        if (api.authenticate && !api.authenticated) {
            return api.showAuthModal('registrationPremium');
        }

        // Download
        savePdf(calculator.title);
    }

    function handleDownloadText() {
        void analytics.trackActionEvent('export_share_clicked', {
            export_action: 'download_text_file',
            resource_id: 'finish_export',
            resource_type: 'tool',
            source: 'finish_export',
        });

        // Check authentication
        if (api.authenticate && !api.authenticated) {
            return api.showAuthModal('registrationPremium');
        }

        // Download
        const lines = [
            `# ${calculator.title}`,
            '',
            `## Experiment Setup`,
            `Population Size: ${calculator.populationSize}`,
            `Confidence: ${calculator.confidenceLevel}%`,
            `Target Margin of Error: ${
                calculator.sample.targetMarginOfError ? `${calculator.sample.targetMarginOfError}%` : 'N/A'
            }`,
            !!calculator.sample.resultMin && `Minimum Range of Expected Result: ${calculator.sample.resultMin}%`,
            !!calculator.sample.resultMax && `Maximum Range of Expected Result: ${calculator.sample.resultMax}%`,
            `Recommended Sample Size: ${calculator.sample.sampleSize || 'N/A'}`,
            '',
            '## Result Analysis',
            `Goal: ${GOALS[calculator.goal]}`,
            `Function: ${DISTRIBUTION_FUNCTIONS[calculator.distributionFunction]}`,
            !!calculator.threshold && `Threshold: ${calculator.threshold || 0}%`,
            `Experiment Result: ${calculator.experimentResult}`,
        ];

        Object.values(calculator.results).forEach((result: Result | null) => {
            if (result) {
                const variant = calculator.variants[result.index];
                const winner = calculator.comparisonResult.winner?.index === result.index;
                lines.push(
                    '',
                    `### ${variant.label}${winner ? ' Winner' : ''}`,
                    `Sample Size: ${variant.sampleSize}`,
                    `Results: ${result.resultPercentage}%`,
                    `Margin of Error: ${result.marginOfErrorPercentage}%`
                );
            }
        });

        const data = lines.filter(line => line !== false).join('\n');

        saveText(calculator.title, data);
    }

    return (
        <>
            <TextField
                aria-label="Name your experiment"
                data-testid="finish-experiment-name"
                label="Name your experiment"
                name="name"
                placeholder="ex. Internal survey"
                value={calculator.name}
                onChange={calculator.setName}
            />
            <div className="position-relative me-5 mt-4 pt-4">
                <div className="position-absolute end-0 bottom-0">
                    <span className="arrow-down" />
                </div>
            </div>
            <div className="d-flex flex-column my-4">
                <Button
                    aria-label="Copy experiment URL"
                    className="mb-3"
                    data-testid="finish-copy-url-button"
                    icon="link"
                    onClick={handleCopyUrl}
                >
                    {copied ? 'Copied! Use this link to return to your experiment.' : 'Copy URL'}
                </Button>
                <Button
                    aria-label="Download experiment text file"
                    className="mb-3"
                    data-testid="finish-download-text-button"
                    icon="notes"
                    onClick={handleDownloadText}
                >
                    Download Text File
                </Button>
                <Button
                    aria-label="Download experiment PDF"
                    data-testid="finish-download-pdf-button"
                    icon="insert-drive-file"
                    onClick={handleDownloadPdf}
                >
                    Download PDF
                </Button>
            </div>
        </>
    );
}
